import { reactive, ref } from 'vue';
import {
    getAddressHints,
    geocodeAddress,
    geocodeCoordinates,
} from '../api/geoService.js';
import { ERROR_MESSAGES } from '../constants/delivery';

export interface IAddressSuggestion {
    uid: number;
    mainValue: string;
    value: string;
    lat: string;
    lng: string;
    street: string;
    house: string;
    zip: string;
    city: string;
    settlement: string;
    korp: string;
    build: string;
    qc_geo: number;
}

export interface IAddress {
    value: string;
    suggestions: IAddressSuggestion[];
    process: boolean;
    valid: boolean;
    msg: string;
}

export interface ICoordinates {
    lat: string;
    lng: string;
}

export function useAddressAutocomplete() {
    const address = reactive<IAddress>({
        value: '',
        suggestions: [],
        process: false,
        valid: false,
        msg: '',
    });

    const coordinates = reactive({
        lat: '',
        lng: '',
    }) as ICoordinates;

    const zip = ref('');
    const city = ref('');
    const settlement = ref('');
    const street = ref('');
    const house = ref('');
    const korp = ref('');
    const build = ref('');
    const qc_geo = ref(5);

    const clearSuggestions = () => {
        address.suggestions = [];
    };

    const fetchSuggestions = (
        query: string,
        locationRestrictions: string[] = [],
        citiesRestrictions: string[] = []
    ) => {
        address.process = true;

        let preparedQuery = query ?? '';

        return getAddressHints(
            { query: preparedQuery },
            locationRestrictions,
            citiesRestrictions
        )
            .then((res) => {
                address.suggestions =
                    res.data?.data?.map((hint: any, index: number) => {
                        return addressHintAdapter(hint, index);
                    }) || [];
            })
            .catch(() => {
                throw new Error(ERROR_MESSAGES.ADDRESS_HINTS_FAILED);
            })
            .finally(() => {
                address.process = false;
            });
    };

    const setSuggestion = (suggestion: IAddressSuggestion) => {
        city.value = suggestion.city;
        settlement.value = suggestion.settlement || '';
        address.value = suggestion.value;
        street.value = suggestion.street;
        house.value = suggestion.house;
        coordinates.lat = suggestion.lat;
        coordinates.lng = suggestion.lng;
        zip.value = suggestion.zip;
        korp.value = suggestion.korp || '';
        build.value = suggestion.build || '';
        qc_geo.value = suggestion.qc_geo;
    };

    const checkAddress = () => {
        return geocodeAddress(address.value)
            .then(({ data }) => {
                coordinates.lat = data.lat;
                coordinates.lng = data.lng;
                return { lat: data.lat, lng: data.lng };
            })
            .catch((e) => {
                console.error(e);
                throw new Error(ERROR_MESSAGES.GEOCODE_FAILED);
            });
    };

    const addressHintAdapter = (
        hint: any,
        index: number
    ): IAddressSuggestion => {
        return {
            uid: Number(hint.uid ?? index),
            mainValue: hint.value,
            value: hint.value,
            lat: hint.lat,
            lng: hint.lng,
            street: hint.street,
            house: hint.house,
            zip: hint.zip,
            city: hint.city,
            qc_geo: hint.data?.qc_geo ? Number(hint.data?.qc_geo) : 5,
            settlement:
                hint.data?.settlement_with_type || hint.data?.settlement || '',
            korp:
                hint.data?.block_type_full === 'корпус'
                    ? hint.data?.block || ''
                    : '',
            build:
                hint.data?.block_type_full === 'строение'
                    ? hint.data?.block || ''
                    : '',
        };
    };

    const checkCoordinates = () => {
        return geocodeCoordinates(coordinates.lat, coordinates.lng)
            .then(({ data }) => {
                if (data.success && data.suggestions?.length > 0) {
                    const suggestion = addressHintAdapter(
                        data.suggestions[0],
                        0
                    );
                    setSuggestion(suggestion);
                    return suggestion;
                }
                return null;
            })
            .catch((e) => {
                console.error(e);
                throw new Error(ERROR_MESSAGES.ADDRESS_HINTS_FAILED);
            });
    };

    const clearAddress = () => {
        address.value = '';
        address.suggestions = [];
        city.value = '';
        settlement.value = '';
        street.value = '';
        house.value = '';
        korp.value = '';
        build.value = '';
        zip.value = '';
        coordinates.lat = '';
        coordinates.lng = '';
    };

    return {
        address,
        coordinates,
        zip,
        city,
        settlement,
        street,
        house,
        korp,
        build,
        clearSuggestions,
        fetchSuggestions,
        setSuggestion,
        checkAddress,
        checkCoordinates,
        clearAddress,
        addressHintAdapter,
    };
}
